import {
  Button,
  ButtonGroup,
  Card,
  ConfirmModal,
  Icon,
  LinkButton,
  LoadingIndicator,
  Page,
} from '@webkom/lego-bricks';
import { usePreparedEffect } from '@webkom/react-prepare';
import { Trash2 } from 'lucide-react';
import { Field } from 'react-final-form';
import { Helmet } from 'react-helmet-async';
import { navigate } from 'vike/client/router';
import {
  EditorField,
  TextInput,
  Form,
  TextArea,
  SelectInput,
  CheckBox,
  Fields,
  ObjectPermissions,
  ImageUploadField,
  LegoFinalForm,
} from '~/components/Form';
import {
  normalizeObjectPermissions,
  objectPermissionsToInitialValues,
} from '~/components/Form/ObjectPermissions';
import { SubmitButton } from '~/components/Form/SubmitButton';
import {
  createArticle,
  deleteArticle,
  editArticle,
  fetchArticle,
} from '~/redux/actions/ArticleActions';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectArticleById } from '~/redux/slices/articles';
import { useCurrentUser } from '~/redux/slices/auth';
import { selectUsersByIds } from '~/redux/slices/users';
import { isNotNullish } from '~/utils';
import { guardLogin } from '~/utils/replaceUnlessLoggedIn';
import { useParams } from '~/utils/useParams';
import { createValidator, required, validYoutubeUrl } from '~/utils/validation';
import type { EditingEvent } from '~/pages/events/utils';
import type { AdminDetailedArticle } from '~/redux/models/Article';

const TypedLegoForm = LegoFinalForm<EditingEvent>;

const validate = createValidator({
  cover: [required('Cover er påkrevd')],
  youtubeUrl: [validYoutubeUrl()],
  title: [required('Tittel er påkrevd')],
  description: [required('Beskrivelse er påkrevd')],
  authors: [required('Forfatter er påkrevd')],
});

type ArticleEditorParams = { articleId: string };

const ArticleEditor = () => {
  const currentUser = useCurrentUser();
  const { articleId } = useParams<ArticleEditorParams>() as ArticleEditorParams;
  const isNew = articleId === undefined;

  const article = useAppSelector((state) =>
    selectArticleById<AdminDetailedArticle>(state, articleId),
  );
  const fetching = useAppSelector((state) => state.articles.fetching);

  let authors = useAppSelector((state) =>
    selectUsersByIds(state, article?.authors),
  );
  if (authors.length === 0 && currentUser) {
    authors = [currentUser];
  }

  const dispatch = useAppDispatch();

  usePreparedEffect(
    'fetchArticleForEditor',
    () => articleId && dispatch(fetchArticle(articleId)),
    [articleId],
  );

  const initialValues = {
    ...article,
    ...objectPermissionsToInitialValues({
      canViewGroups: article?.canViewGroups,
      canEditGroups: article?.canEditGroups,
      canEditUsers: article?.canEditUsers,
    }),
    content: article?.content || '',
    authors: authors
      .filter(isNotNullish)
      .map((user) => ({ ...user, label: user.fullName, value: user.id })),
    tags: (article?.tags || []).map((tag) => ({
      label: tag,
      value: tag,
    })),
  };

  if (!isNew && (!article || !article.content || fetching)) {
    return <LoadingIndicator loading />;
  }

  const onSubmit = (data) => {
    const body = {
      ...(data.cover
        ? {
            cover: data.cover,
          }
        : {}),
      ...normalizeObjectPermissions(data),
      authors: data.authors.map((e) => e.value),
      youtubeUrl: data.youtubeUrl,
      title: data.title,
      description: data.description,
      content: data.content,
      tags: (data.tags || []).map((tag) => tag.value.toLowerCase()),
      pinned: data.pinned,
    };

    dispatch(
      isNew ? createArticle(body) : editArticle({ id: articleId, ...body }),
    ).then((res) => {
      navigate(
        isNew ? `/articles/${res.payload.result}/` : `/articles/${articleId}`,
      );
    });
  };

  const handleDeleteArticle = async () => {
    await dispatch(deleteArticle(articleId)).then(() => {
      navigate('/articles/');
    });
  };

  const title = isNew ? 'Ny artikkel' : 'Redigerer: ' + article?.title;

  return (
    <Page
      title={title}
      skeleton={fetching}
      back={{
        href: `/articles/${isNew ? '' : articleId}`,
      }}
    >
      <Helmet title={title} />

      <TypedLegoForm
        onSubmit={onSubmit}
        validate={validate}
        initialValues={initialValues}
      >
        {({ handleSubmit, values }) => (
          <Form onSubmit={handleSubmit}>
            <Field
              name="cover"
              component={ImageUploadField}
              aspectRatio={20 / 6}
              img={article && article.cover}
            />
            <Field
              name="youtubeUrl"
              label="Erstatt cover-bildet med video fra YouTube"
              description="Videoen erstatter ikke coveret i listen over artikler"
              placeholder="https://www.youtube.com/watch?v=bLHL75H_VEM&t=5"
              component={TextInput.Field}
            />
            <Field
              label="Festet på forsiden"
              name="pinned"
              type="checkbox"
              component={CheckBox.Field}
            />
            {values.pinned && (
              <Card severity="warning">
                <Card.Header>Obs!</Card.Header>
                <p>
                  Du må ha godkjenning fra Ledelsen for å feste til forsiden.
                </p>
              </Card>
            )}

            <Field
              placeholder="Title"
              name="title"
              label="Tittel"
              component={TextInput.Field}
              id="article-title"
              required
            />

            <Field
              name="tags"
              label="Tags"
              filter={['tags.tag']}
              placeholder="Skriv inn tags"
              component={SelectInput.AutocompleteField}
              isMulti
              tags
            />

            <Field
              placeholder="Velg forfattere"
              name="authors"
              label="Forfattere"
              component={SelectInput.AutocompleteField}
              isMulti
              filter={['users.user']}
              id="article-title"
              required
            />

            <Fields
              names={[
                'requireAuth',
                'canViewGroups',
                'canEditUsers',
                'canEditGroups',
              ]}
              component={ObjectPermissions}
            />

            <Field
              placeholder="En kort beskrivelse av artikkelen"
              name="description"
              label="Beskrivelse"
              component={TextArea.Field}
              id="article-title"
              required
            />

            <Field
              placeholder="Skriv artikkelen din her ..."
              name="content"
              label="Innhold"
              component={EditorField.Field}
            />

            <ButtonGroup>
              <LinkButton flat href={`/articles/${isNew ? '' : articleId}`}>
                Avbryt
              </LinkButton>
              <SubmitButton>
                {!isNew ? 'Lagre endringer' : 'Opprett'}
              </SubmitButton>
              {!isNew && (
                <ConfirmModal
                  title="Slett artikkelen"
                  message="Er du sikker på at du vil slette artikkelen?"
                  onConfirm={handleDeleteArticle}
                >
                  {({ openConfirmModal }) => (
                    <Button onPress={openConfirmModal} danger>
                      <Icon iconNode={<Trash2 />} size={19} />
                      Slett artikkel
                    </Button>
                  )}
                </ConfirmModal>
              )}
            </ButtonGroup>
          </Form>
        )}
      </TypedLegoForm>
    </Page>
  );
};

export default guardLogin(ArticleEditor);
